import * as React from 'react';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Map,
  Marker,
  Popup,
  NavigationControl,
  ScaleControl,
  GeolocateControl,
  Source,
  Layer,
  LogoControl,
  MapProvider,
  useControl,
  useMap,
  AttributionControl
} from 'react-map-gl/maplibre';
import { Modal, Button } from 'react-bootstrap';
import goldfinderstyle from './map-gl/goldfinder-style.json';
import '@geoman-io/maplibre-geoman-free/dist/maplibre-geoman.css';

import { TerraDrawPointMode, TerraDrawRectangleMode, TerraDrawCircleMode } from 'terra-draw';

// Import optimized overlay components
import { TenementOverlayMapLibreOptimized } from './tenements/TenementOverlayOptimized';
import { AmalgamationsPendingOverlay } from './tenements/AmalgamationsPendingOverlay';
import { AssayOverlayMapLibreOptimized } from './soil-assay/AssayOverlayOptimized';
// Constants only - the popup builder stays behind a dynamic import so the
// non-optimised module is not pulled into the initial bundle.
import { SOIL_TILE_SOURCE, SOIL_TILE_BACKGROUND_LAYER } from './soil-assay/AssayOverlay';
import { DH_TILE_SOURCE, DH_TILE_BACKGROUND_LAYER } from './drill-hole/DrillHoleOverlayOptimized';
import { MinedexOverlayMapLibre } from './minedex/MinedexOverlay';
import { ProspectOverlay } from './prospect/ProspectOverlay';
import { defaultProspectFilters } from './prospect/ProspectFilter';
import { INTERACTIVE as PROSPECT_INTERACTIVE } from './prospect/prospectStyle';
import { OcrGoldOverlay } from './ocr-gold/OcrGoldOverlay';
import { OCR_GOLD_INTERACTIVE } from './ocr-gold/ocrGoldStyle';
import { AsxAlluvialOverlay } from './asx-alluvial/AsxAlluvialOverlay';
import { ALLUVIAL_SIGNAL_INTERACTIVE } from './asx-alluvial/asxAlluvialStyle';
import { BlockGridOverlay } from './grid/BlockGridOverlay';
import { PlatesOverlay } from './plates/PlatesOverlay';
import { PlatesPanel } from './plates/PlatesPanel';
  import { DrillHoleOverlayMapLibreOptimized } from './drill-hole/DrillHoleOverlayOptimized'
  import { GraticlesOverlayLibre } from './graticles/GraticlesOverlay';
import { Section19OverlayLibre } from './Section19/Section19Overlay';

// Import utilities
import { wfsCache, debounce, requestManager, expandBbox, bboxContains } from '../utils/wfsCache';
import { Events } from '../firebase/telemetry';
import { SideBar } from './map-gl/SideBar';
import { SideBar40E } from './map-gl/SideBar40E';
import { SampleReporter } from './soil-assay/SampleReporter';
import { DHSampleReporter } from './drill-hole/DHSampleReporter';

// Import existing components and utilities
import { getDefaultPreset, DEFAULT_PRESET_ID } from './map-gl/presets/defaultPreset';
import { buildPresetPayloadFromCurrentState, deepClone } from './map-gl/presets/presetSerialization';
import { useOverlayPresets } from '../hooks/useOverlayPresets';
import { PresetsControl } from './map-gl/PresetsControl';

import * as turf from "@turf/turf";
import UTMLatLng from 'utm-latlng';
import { GeocodingControl } from "@maptiler/geocoding-control/react";
import { createMapLibreGlMapController } from "@maptiler/geocoding-control/maplibregl-controller";
import maplibregl from 'maplibre-gl';
import "@maptiler/geocoding-control/style.css";
import { Collapse } from 'react-bootstrap';
import { CreateDrawPointPopup, DrawControl } from './map-gl/DrawControl';
import { MaplibreTerradrawControl } from '@watergis/maplibre-gl-terradraw';
import { AlertList } from "react-bs-notifier";
import classNames from 'classnames';
import { useMapViewPersistence } from '../hooks/useMapViewPersistence';
import { useOverlayTelemetry, useFilterTelemetry } from '../hooks/useOverlayTelemetry';
import { FloatingMenu, SidebarToggle, MobileSidebarToggle } from './map-gl/FloatingMenu';
import {
  buildSentinelTiles,
  buildDatedTiles,
  styleForSensor,
  buildBarestEarthTiles,
  buildLandChangeTiles,
  buildLandChangeInfoUrl,
  SCAN_SENSORS,
  buildDeaOverlayTiles,
  DEA_ATTRIBUTION,
  DEA_OVERLAYS,
  SENTINEL_MIN_ZOOM,
  SENTINEL_MAX_ZOOM,
  BACKDROP_MIN_ZOOM,
  TERRAIN_TILES,
  TERRAIN_ATTRIBUTION,
  TERRAIN_MAX_ZOOM,
} from './imagery/sentinelSource';
import { useSentinelDates, DATE_RANGES } from './imagery/useSentinelDates';
import { SentinelTimeBar } from './imagery/SentinelTimeBar';
import { CursorPosition } from './map-gl/CursorPosition';
import { MapSearch } from './search/MapSearch';
import { WAYPOINT_PIN_LAYER } from './map-gl/waypointPin';
import { useCollapsedAttribution } from './map-gl/useCollapsedAttribution';
import { ImageryAgeChip } from './imagery/ImageryAgeChip';
import { LandChangeLegend } from './imagery/LandChangeLegend';
import { WaGeologyOverlay, WA_FAULT_LAYER_IDS, faultScale } from './geology/WaGeologyOverlay';
import { identifyWaGeology } from './geology/waGeology';
import { createWaGeologyPopup } from './geology/GeologyPopup';
import { loadImageryPrefs, saveImageryPrefs } from './imagery/imageryPrefs';
import { addMinedexSymbols, MINEDEX_LAYER_IDS } from './minedex/dmirsSymbols';

/**
 * 500k interpreted bedrock legend in the sidebar. TEMPORARILY hidden
 * 2026-08-26: the service returns a 1543x4096 PNG (~900KB) because the layer
 * has hundreds of mapped units, which dominates a 336px sidebar even inside a
 * scroll box. The layer itself is unaffected; only its key is hidden.
 * See web-app/docs/TEMPORARILY-DISABLED.md.
 */
export const SHOW_BEDROCK_LEGEND = false;

/**
 * How much ground to fetch around the viewport. 2 = double the width and
 * height, so 4x the area.
 *
 * Measured on tenements around Kalgoorlie: 1.0x = 3.14 MB / 3,194 features,
 * 2.0x = 5.74 MB / 5,913. Only +83% payload for 4x the coverage, because
 * feature density drops off outside the cluster being looked at. Raising this
 * further hits diminishing returns and starts costing real time on the wire.
 */
const WFS_FETCH_FACTOR = 2;

/**
 * Layers whose features behave like buttons - clicking one opens its popup.
 * Only these get the pointer cursor.
 *
 * Tenements, graticules, faults and Section 19 are deliberately absent. They
 * are polygons covering most of the view most of the time, so a pointer over
 * them is indistinguishable from the grab-hand this replaced.
 */
const POINT_HOVER_LAYERS = new Set([
  SOIL_TILE_BACKGROUND_LAYER,
  DH_TILE_BACKGROUND_LAYER,
  WAYPOINT_PIN_LAYER,
  ...MINEDEX_LAYER_IDS,
]);


/**
 * Sentinel-2 Barest Earth, as a base map AND as the Change & Vegetation
 * overlay - both draw the same `s2_barest_earth` product. TEMPORARILY hidden
 * 2026-08-28: DEA publishes it with no time dimension and the composite is
 * frozen at September 2020, so it silently ages while looking current.
 * See web-app/docs/TEMPORARILY-DISABLED.md.
 */
export const SHOW_BAREST_EARTH = false;

/**
 * Detected changes 1991-2023 (the derived Land Change layer). TEMPORARILY
 * hidden 2026-09-25. Off here as well as in the sidebar, so a browser that last
 * had it switched on cannot keep drawing it with no control to turn it off.
 */
export const SHOW_LAND_CHANGE = false;

/**
 * The Vegetation & Water DEA group (NDVI, fractional cover, water).
 * TEMPORARILY hidden 2026-09-25, in the sidebar and on the map alike.
 */
export const SHOW_VEG_WATER = false;
export const VEG_WATER_DEA_IDS = ['deaNdvi', 'deaFractionalCover', 'deaWater'];

/**
 * The Interpreted Bedrock base-map group (500k state layer). TEMPORARILY
 * hidden 2026-08-28. The layer and its identify path in geology/waGeology.js
 * are untouched; only the base-map option is hidden.
 * See web-app/docs/TEMPORARILY-DISABLED.md.
 */
export const SHOW_INTERPRETED_BEDROCK = false;

/**
 * Land Change starting state: the preset's value, overlaid with whatever this
 * browser last had switched on. Presets that explicitly carry landChange still
 * win — this only fills the gap for presets saved before it existed, and for
 * the Default preset, which is what most sessions load.
 */
function landChangeFromPrefs(base) {
  const saved = loadImageryPrefs();
  return {
    ...base,
    visible: saved.landChangeVisible ?? base.visible,
    opacity: saved.landChangeOpacity ?? base.opacity,
  };
}

// Initial view state is now provided by useMapViewPersistence hook

// Year of the EOX Sentinel-2 cloudless mosaic used for the Sentinel-2 base layer.
// Available: 2018..2024 (2018 is the last CC BY 4.0 edition; 2019+ are CC BY-NC-SA 4.0).

// ASTER mineral-map overlays served from the National ASTER Geoscience Maps
// (CSIRO / Geoscience Australia) via the NCI GSKY WMS (gsky.nci.org.au/ows/aster).
// `id` = internal source/layer id + asterVisibility key; `wms` = WMS layer name;
// `label` = sidebar text. Gold-prospecting-relevant subset (see gold-finder#121);
// the full service exposes 14 products — enable more here as needed.
export const ASTER_LAYERS = [
  { id: 'asterFerricOxideContent',     wms: 'Ferric_Oxide_Content',     label: 'Ferric Oxide Content (gossans/ironstone)' },
  { id: 'asterFerricOxideComposition', wms: 'Ferric_Oxide_Composition', label: 'Ferric Oxide Composition (hematite/goethite)' },
  { id: 'asterFeOHGroupContent',       wms: 'FeOH_Group_Content',       label: 'FeOH Group Content (jarosite/gossan)' },
  { id: 'asterAlOHGroupContent',       wms: 'AlOH_Group_Content',       label: 'AlOH Group Content (sericite alteration)' },
  { id: 'asterAlOHGroupComposition',   wms: 'AlOH_Group_Composition',   label: 'AlOH Group Composition (alteration vectoring)' },
  { id: 'asterKaolinGroupIndex',       wms: 'Kaolin_Group_Index',       label: 'Kaolin Group Index (argillic alteration)' },
  { id: 'asterMgOHGroupContent',       wms: 'MgOH_Group_Content',       label: 'MgOH Group Content (chlorite/carbonate)' },
  { id: 'asterSilicaIndex',            wms: 'Silica_Index',             label: 'TIR Silica Index (silicification)' },
];

// Authoritative legend for an ASTER layer, straight from the WMS
// GetLegendGraphic op — guarantees the key matches the rendered false-colour.
/**
 * GetLegendGraphic for a SLIP Geology and Soils layer. The 500k interpreted
 * bedrock key is large (1543x4096 - hundreds of mapped units), so wherever it
 * renders it needs its own scroll container rather than being scaled to fit.
 */
export const slipGeologyLegendUrl = (wmsLayer) =>
  'https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/' +
  'Geology_and_Soils_Map/MapServer/WMSServer?service=WMS&version=1.3.0' +
  `&request=GetLegendGraphic&format=image/png&layer=${wmsLayer}`;

export const asterLegendUrl = (wmsLayer) =>
  `https://gsky.nci.org.au/ows/aster?service=WMS&version=1.1.1&request=GetLegendGraphic&layer=${wmsLayer}&format=image/png`;

export const emptyGeoJsonGraticles = {
  "type": "FeatureCollection",
  "name": "graticules",
  "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
  "features": []
};

/**
 * Optimized MapGL component with improved performance for WFS queries
 */
export default function MapGlOptimized({
  isSideBarShown,
  mobileFiltersOpen,
  setSideBarShown,
  onToggleSidebar,
  onOpenAccountSettings,
  onMobileFiltersToggle
}) {
  // Map view persistence - restores user's last viewport position
  const { initialViewState, isLoading: isViewportLoading, saveViewport } = useMapViewPersistence();

  const [alerts, setAlerts] = React.useState([]);

  const onDismissed = useCallback(alert => {
    setAlerts(alerts => {
      const idx = alerts.indexOf(alert);
      if (idx < 0) return alerts;
      return [...alerts.slice(0, idx), ...alerts.slice(idx + 1)];
    });
  }, []);

  // State management — initializers derive from the single source of truth
  // in `getDefaultPreset()`. This ensures the hardcoded defaults here and the
  // built-in "Default" preset can never drift apart when new fields are added.
  const [tenementFilters, setTenementFilters] = useState(() => getDefaultPreset().tenementFilters);
  const [minedexFilters, setMinedexFilters] = useState(() => getDefaultPreset().minedexFilters);
  const [assayFilters, setAssayFilters] = useState(() => getDefaultPreset().assayFilters);
  const [drillHolefilters, setDrillHoleFilters] = useState(() => getDefaultPreset().drillHoleFilters);
  // Which kinds of prospect mark are drawn: soils, drill holes, findings.
  //
  // NOT part of the preset schema, unlike the four above. The overlay it filters
  // is admin-only internal working material, and presets are a user feature that
  // would have to carry a field nobody outside the team can set — plus a schema
  // bump to do it. It resets to everything on with a reload, which for a control
  // used while reading one report is the right default anyway.
  const [prospectFilters, setProspectFilters] = useState(defaultProspectFilters);

  const emptyGeoJson = {
    "type": "FeatureCollection",
    "name": "Mining_Tenements_DMIRS_003",
    "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
    "features": []
  };

  // Data state
  const [tenementGeoJson, setTenementGeojson] = useState(emptyGeoJson);
  const [assayGeoJson, setAssayGeojson] = useState(emptyGeoJson);
  const [graticleGeoJson, setGraticleGeoJson] = useState(emptyGeoJson);
  const [section19GeoJson, setSection19GeoJson] = useState(emptyGeoJson);
  const [drillHoleGeoJson, setDrillHoleGeoJson] = useState(emptyGeoJson);
  const [amalgamationsPendingGeoJson, setAmalgamationsPendingGeoJson] = useState(emptyGeoJson);

  // Popup state
  const [tenementPopup, setTenementPopup] = useState(<></>);
  const [showTenementPopup, setShowTenementPopup] = useState(false);
  const [selectedTenement, setSelectedTenement] = useState({ properties: { gid: 99999999 } });
  const [selectedAmalgamation, setSelectedAmalgamation] = useState({ properties: { OBJECTID: 99999999 } });
  const [geologyPopup, setGeologyPopup] = useState(<></>);
  const [showGeologyPopup, setShowGeologyPopup] = useState(false);
  const [assayPopup, setAssayPopup] = useState(<></>);
  const [showAssayPopup, setShowAssayPopup] = useState(false);
  const [reportAssayModalShow, setReportAssayModalShow] = useState(false);
  const [reportedAssay, setReportedAssay] = useState({ properties: {} });
  const [minedexPopup, setMinedexPopup] = useState(<></>);
  const [showMinedexPopup, setShowMinedexPopup] = useState(false);
  const [graticlePopup, setGraticlePopup] = useState(<></>);
  const [showGraticlePopup, setShowGraticlePopup] = useState(false);
  const [section19Popup, setSection19Popup] = useState(<></>);
  const [showSection19Popup, setShowSection19Popup] = useState(false);
  const [drillHolePopup, setDrillHolePopup] = useState(<></>);
  const [reportDhModalShow, setReportDhModalShow] = useState(false);
  const [reportedDh, setReportedDh] = useState({ properties: {} });
  const [showDrillHolePopup, setShowDrillHolePopup] = useState(false);
  const [drawPointPopup, setDrawPointPopup] = useState(<></>);
  const [showDrawPointPopup, setShowDrawPointPopup] = useState(false);

  // Other state
  const [API_KEY] = useState('WCXZHw7CwlQPRosD6glL');
  const [mapController, setMapController] = useState();
  const [mousePoint, setMousePoint] = useState("");
  const [mouseLngLat, setMouseLngLat] = useState(null);
  const [hoveringPoint, setHoveringPoint] = useState(false);
  const [drawMode, setDrawMode] = useState(null);
  // Bumped whenever TerraDraw's store changes, so the sidebar's waypoint count
  // and export re-read it. The store itself is not React state.
  // What each layer last FETCHED - the expanded box, not the viewport - plus a
  // signature of the filters it was fetched with. A pan that stays inside the
  // fetched box with unchanged filters needs no request at all.
  const fetchedRef = React.useRef({});

  const [waypointRevision, setWaypointRevision] = useState(0);
  const bumpWaypoints = useCallback(() => setWaypointRevision(n => n + 1), []);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [is40ESideBarShown, setIs40ESideBarShown] = useState(false);
  const [selectedGraticleStack, setSelectedGraticuleStack] = useState(emptyGeoJsonGraticles);
  const [tenement40E, setTenement40E] = useState(null);
  const [show40EConfirmModal, setShow40EConfirmModal] = useState(false);
  const [pendingTenement40E, setPendingTenement40E] = useState(null);
  const [assayMarkerColourSettings, setAssayMarkerColourSettings] = useState(() => getDefaultPreset().assayMarkerColourSettings);
  const [drillHoleMarkerColourSettings, setDrillHoleMarkerColourSettings] = useState(() => getDefaultPreset().drillHoleMarkerColourSettings);

  // Layer visibility state — derived from getDefaultPreset() (single source of truth)
  const [layerVisibility, setLayerVisibility] = useState(() => getDefaultPreset().layerVisibility);
  const [overlayVisibility, setOverlayVisisbility] = useState(() => getDefaultPreset().overlayVisibility);

  // Set immediately before a preset is applied so the resulting toggle burst is
  // labelled source='preset' rather than masquerading as deliberate clicks.
  const changeSourceRef = React.useRef(false);
  // The first preset application is the one that runs on mount, before the
  // user has chosen anything. Remembered view state should survive it.
  const presetAppliedOnceRef = React.useRef(false);

  // Which mutually-exclusive base layer is showing, for the map_view dimension.
  // Mirrored into a ref so the debounced moveend handler never closes over a
  // stale value.
  /**
 * Sentinel-2 bottom-of-map UI. See web-app/docs/TEMPORARILY-DISABLED.md.
 *
 *  - IMAGERY_AGE_CHIP: the pill reading "Newest clear Sentinel-2 here: …",
 *    shown over every base map. TEMPORARILY hidden 2026-08-26.
 *  - SENTINEL_TIME_BAR: the date/sensor/style picker, shown only while the
 *    dated Sentinel-2 base map is selected. Restored 2026-08-26 - it is the
 *    only control for sensor and date, so without it a remembered Landsat
 *    selection could not be undone.
 */
const SHOW_IMAGERY_AGE_CHIP = false;

const SHOW_SENTINEL_TIME_BAR = true;

const BASE_MAP_KEYS = ['sentinel2Dated', 'sentinel2', 'hybrid', 'topo', 'geo100', 'geo200', 'bedrock500', 'regolith', 'tectonic', 'magnetic', 'street'];
  const layerVisibilityRef = React.useRef(layerVisibility);
  layerVisibilityRef.current = layerVisibility;
  const activeBaseMap = useCallback(
    () => BASE_MAP_KEYS.find(k => layerVisibilityRef.current?.[k]) || 'none',
    []
  );

  // Last reported zoom band + timestamp, so map_view stays rate-limited.
  const lastMapViewRef = React.useRef({ band: null, at: 0 });

  // --- Sentinel-2 (dated) -------------------------------------------------
  // Viewport in EPSG:4326, kept only so the STAC catalogue can be asked what
  // acquisitions exist HERE. A national date list is useless: a date with no
  // overpass over this bbox renders a 155-byte transparent PNG.
  const [viewBbox, setViewBbox] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);
  // Two raster buffers, cross-faded. Swapping tiles on a single source blanks
  // the map for the ~1.5s DEA takes to answer, which reads as a broken map.
  const [s2BufferDates, setS2BufferDates] = useState({ a: null, b: null });
  const [s2Active, setS2Active] = useState('a');
  const [s2Pending, setS2Pending] = useState(null);
  // View preferences (date/style/cloud/range) — per browser, never in a preset.
  const [imageryPrefs, setImageryPrefs] = useState(() => loadImageryPrefs(SHOW_SENTINEL_TIME_BAR));

  // Stop the attribution panel re-opening every time the base map changes.
  useCollapsedAttribution();
  const [imageryPlaying, setImageryPlaying] = useState(false);
  const [imagerySnapNote, setImagerySnapNote] = useState(null);

  // DEA change/vegetation + interpretive terrain overlays (M4/M5)
  const [deaVisibility, setDeaVisibility] = useState(() => getDefaultPreset().deaVisibility);
  const [deaOpacity, setDeaOpacity] = useState(() => getDefaultPreset().deaOpacity);
  const [deaOptions, setDeaOptions] = useState(() => getDefaultPreset().deaOptions);
  const [landChange, setLandChange] = useState(() => landChangeFromPrefs(getDefaultPreset().landChange));
  const [landChangePopup, setLandChangePopup] = useState(null);

  // Remember the Land Change toggle per browser, beside the viewport. This
  // goes through imageryPrefs state rather than straight to localStorage:
  // updateImageryPrefs rebuilds the whole object from state, so a later date
  // change would otherwise write back a stale toggle and undo this.
  useEffect(() => {
    const opacity = landChange.opacity ?? null;
    setImageryPrefs(prev => {
      if (prev.landChangeVisible === landChange.visible && prev.landChangeOpacity === opacity) {
        return prev;
      }
      const next = { ...prev, landChangeVisible: landChange.visible, landChangeOpacity: opacity };
      saveImageryPrefs(next);
      return next;
    });
  }, [landChange.visible, landChange.opacity]);
  // Declared here so the preset-application effect below compiles; the
  // telemetry work reads and clears it to label toggles source='preset'.
  const [terrain, setTerrain] = useState(() => getDefaultPreset().terrain);

  // ASTER mineral-map overlays (blend-on-top rasters + shared opacity)
  const [asterVisibility, setAsterVisibility] = useState(() => getDefaultPreset().asterVisibility);
  const [asterOpacity, setAsterOpacity] = useState(() => getDefaultPreset().asterOpacity);

  // Observes every visibility group — base maps, overlays, ASTER, DEA, terrain —
  // and reports toggles, a settled state snapshot and per-layer engagement time.
  // Placed after all of that state is declared; adding a new visibility group
  // means adding it here, not instrumenting its setter.
  useOverlayTelemetry({
    layerVisibility,
    overlayVisibility,
    asterVisibility,
    deaVisibility,
    terrain,
    zoom: mapZoom,
    asterOpacity,
    deaOpacity,
    deaOptions,
    imageryPrefs,
    changeSourceRef,
  });

  // Debounced, so a slider drag reports the value settled on, not every tick.
  useFilterTelemetry({
    assayFilters,
    drillHoleFilters: drillHolefilters,
    tenementFilters,
    minedexFilters,
  });

  // Overlay presets — per-user saved configurations
  const {
    presets,
    activePresetId,
    setActivePresetId,
    isLoading: isPresetsLoading,
    savePresetAsNew,
    updatePreset,
    renamePreset,
    deletePreset,
    getPresetById,
    canSaveMore,
    userLoggedIn: presetsUserLoggedIn,
    userPresetCount,
    maxUserPresets,
  } = useOverlayPresets();

  // Apply preset payload to local state whenever the active preset changes.
  // Waits for presets to finish loading so a restored localStorage id can
  // actually resolve to a preset entry.
  useEffect(() => {
    if (isPresetsLoading) return;
    let payload;
    if (activePresetId === DEFAULT_PRESET_ID) {
      payload = getDefaultPreset();
    } else {
      const entry = getPresetById(activePresetId);
      if (!entry) {
        // Active id pointed at a preset that no longer exists — fall back to Default.
        setActivePresetId(DEFAULT_PRESET_ID);
        return;
      }
      payload = entry.payload;
    }
    // Deep-clone before applying so local state never shares references with
    // the in-memory preset cache. Some editors (e.g. the marker colour pickers)
    // mutate elements in place, which would otherwise corrupt the stored preset.
    const applied = deepClone(payload);
    // Read (and cleared) by useOverlayTelemetry on the render this triggers, so
    // the resulting toggles are labelled source='preset'.
    changeSourceRef.current = true;
    Events.presetApplied(activePresetId === DEFAULT_PRESET_ID ? 'default' : 'user');
    setOverlayVisisbility(applied.overlayVisibility);
    setLayerVisibility(applied.layerVisibility);
    // Fall back to defaults for presets saved before ASTER support existed.
    setAsterVisibility(applied.asterVisibility ?? getDefaultPreset().asterVisibility);
    setAsterOpacity(applied.asterOpacity ?? getDefaultPreset().asterOpacity);
    // Presets saved before the imagery work have none of these.
    setDeaVisibility(applied.deaVisibility ?? getDefaultPreset().deaVisibility);
    setDeaOpacity(applied.deaOpacity ?? getDefaultPreset().deaOpacity);
    setDeaOptions(applied.deaOptions ?? getDefaultPreset().deaOptions);
    // On mount, this browser's remembered Land Change toggle beats the preset:
    // the Default preset carries landChange:false, so honouring it here would
    // switch the layer off on every visit no matter what the user left on.
    // Once the user actually picks a preset, the preset wins.
    setLandChange(
      presetAppliedOnceRef.current
        ? applied.landChange ?? landChangeFromPrefs(getDefaultPreset().landChange)
        : landChangeFromPrefs(applied.landChange ?? getDefaultPreset().landChange)
    );
    presetAppliedOnceRef.current = true;
    setTerrain(applied.terrain ?? getDefaultPreset().terrain);
    setTenementFilters(applied.tenementFilters);
    setMinedexFilters(applied.minedexFilters);
    setAssayFilters(applied.assayFilters);
    setDrillHoleFilters(applied.drillHoleFilters);
    setAssayMarkerColourSettings(applied.assayMarkerColourSettings);
    setDrillHoleMarkerColourSettings(applied.drillHoleMarkerColourSettings);
  }, [activePresetId, isPresetsLoading, getPresetById, setActivePresetId]);

  const buildCurrentPayload = useCallback(
    () =>
      buildPresetPayloadFromCurrentState({
        overlayVisibility,
        layerVisibility,
        asterVisibility,
        asterOpacity,
        deaVisibility,
        deaOpacity,
        deaOptions,
        landChange,
        terrain,
        tenementFilters,
        minedexFilters,
        assayFilters,
        drillHoleFilters: drillHolefilters,
        assayMarkerColourSettings,
        drillHoleMarkerColourSettings,
      }),
    [
      overlayVisibility,
      layerVisibility,
      asterVisibility,
      asterOpacity,
      deaVisibility,
      deaOpacity,
      deaOptions,
      landChange,
      terrain,
      tenementFilters,
      minedexFilters,
      assayFilters,
      drillHolefilters,
      assayMarkerColourSettings,
      drillHoleMarkerColourSettings,
    ]
  );

  const onPresetError = useCallback(
    (err) => {
      setAlerts((prev) => [
        ...prev,
        {
          id: new Date().getTime(),
          type: 'danger',
          headline: 'Preset error',
          message: err?.message || 'An unexpected error occurred.',
        },
      ]);
    },
    []
  );

  // Drawing state
  // Waypoints are drawn as GoldFinder pins from our own source (see
  // map-gl/waypointPin.js), so TerraDraw's built-in disc is turned off here
  // rather than hidden afterwards. Chasing its layers to set circle-opacity
  // did not hold: it names them differently between versions and re-adds them
  // on every mode change. Zero-width is configuration, and it stays put.
  const [drawpointMode, setDrawPointMode] = useState(new TerraDrawPointMode({
    styles: {
      pointWidth: 0,
      pointOutlineWidth: 0,
      pointColor: 'rgba(0,0,0,0)',
      pointOutlineColor: 'rgba(0,0,0,0)',
      editedPointWidth: 0,
      editedPointOutlineWidth: 0,
      editedPointColor: 'rgba(0,0,0,0)'
    }
  }));

  // 'select' and 'download' have no toolbar buttons any more (hidden in
  // styles/map-controls.css) but both stay registered: terra-draw's
  // selectFeature() resolves the select mode or throws, which is what the
  // marker popup's Move button rides on, and dropping 'download' would take
  // the export the sidebar now offers with it.
  //
  // 'delete-selection' is gone entirely. TerraDraw only un-hides that button
  // while the instance is in select mode, which a user can no longer enter -
  // the sole path there is the popup's Move, where a delete button appearing
  // mid-drag alongside the popup's own Done would be a second way to do a
  // thing the popup already does. Deleting a waypoint lives in its popup.
  const [drawCtrl, setDrawctrl] = useState(new MaplibreTerradrawControl({
    modes: ['point', 'select', 'download'],
    open: true,
    modeOptions: {
      point: drawpointMode
    }
  }));

  var utm = new UTMLatLng();

  // Keep existing functions (trigger40EWizard, onMapClick, etc.)
  const trigger40EWizard = (tenement) => {
    setPendingTenement40E(tenement);
    setShow40EConfirmModal(true);
  };

  const confirm40EWizard = () => {
    setIs40ESideBarShown(true);
    setSideBarShown(false);
    setTenement40E(pendingTenement40E);
    setShowTenementPopup(false);
    setOverlayVisisbility({ ...overlayVisibility, graticles: true });
    
    const bounds = turf.bbox(pendingTenement40E);
    var wfsbbox = bounds.toString();
    import('./graticles/GraticlesOverlay').then(module =>
      module.queryGraticlesWFSLibreWithPolygonFilter(wfsbbox, setGraticleGeoJson, pendingTenement40E)
    );
    
    setShow40EConfirmModal(false);
    setPendingTenement40E(null);
  };

  const cancel40EWizard = () => {
    setShow40EConfirmModal(false);
    setPendingTenement40E(null);
  };

  /**
   * Land Change is a WMS raster, so a click can't hit a vector feature -- ask
   * the server what is under the pixel. GetFeatureInfo needs the click as an
   * offset inside a described map request, so the map's current bounds and
   * canvas size go along with it.
   */
  const queryLandChange = useCallback(async event => {
    const map = event.target;
    const canvas = map.getCanvas();
    const b = map.getBounds();
    const toMerc = ([lng, lat]) => {
      const x = (lng * 20037508.34) / 180;
      const y =
        ((Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180)) * 20037508.34) / 180;
      return [x, y];
    };
    const [minx, miny] = toMerc([b.getWest(), b.getSouth()]);
    const [maxx, maxy] = toMerc([b.getEast(), b.getNorth()]);
    const url = buildLandChangeInfoUrl({
      bbox3857: [minx, miny, maxx, maxy],
      width: canvas.clientWidth,
      height: canvas.clientHeight,
      x: event.point.x,
      y: event.point.y,
    });
    try {
      const res = await fetch(url);
      const body = await res.json();
      const features = body.features || [];
      if (!features.length) { setLandChangePopup(null); return; }
      setLandChangePopup({
        lngLat: event.lngLat,
        features: features.map(f => f.properties),
      });
    } catch (err) {
      // A failed lookup must not swallow the click or blank an existing popup.
      console.warn('land change lookup failed', err);
    }
  }, []);

  // Import and use existing onMapClick function
  const onMapClick = useCallback(event => {
    // The prospect overlay draws above everything else and answers its own
    // clicks, from a handler ProspectOverlay puts on the map directly. That
    // handler was registered after this one and so runs after it, which means
    // it cannot call anything off: without this bail, clicking a target also
    // opens whatever tenement, soil sample or drill hole sits underneath, and
    // fires the geology identify as well.
    //
    // queryRenderedFeatures throws on a layer that is not on the map, and these
    // come and go with the overlay, so filter to the ones that exist first.
    // The OCR gold and ASX alluvial overlays answer their own clicks the same way.
    const prospectHitLayers = [...PROSPECT_INTERACTIVE, ...OCR_GOLD_INTERACTIVE, ...ALLUVIAL_SIGNAL_INTERACTIVE].filter(id => event.target.getLayer(id));
    if (prospectHitLayers.length
        && event.target.queryRenderedFeatures(event.point, { layers: prospectHitLayers }).length) {
      return;
    }

    if (SHOW_LAND_CHANGE && landChange.visible) queryLandChange(event);
    if (is40ESideBarShown) {
      const selectedgraticle = event.features.filter(e => e.source === 'graticle')
      const selectedTenementsGrat = event.features.filter(e => (e.source === 'tenements') && (e.properties.fmt_tenid === tenement40E.properties.fmt_tenid))

      if ((selectedgraticle.length !== 0) && (selectedTenementsGrat.length != 0)) {
        const tempStack = emptyGeoJson;
        tempStack.features = selectedGraticleStack.features.filter(e =>
          !((e.properties.block_no === selectedgraticle[0].properties.block_no)
            && (e.properties.primary_no === selectedgraticle[0].properties.primary_no))
        )

        if (tempStack.features.length !== selectedGraticleStack.features.length) {
          setSelectedGraticuleStack(tempStack)
        } else if (tempStack.features.length < 10) {
          if (tempStack.features.length === 0) {
            tempStack.features.push(selectedgraticle[0])
            setSelectedGraticuleStack(tempStack)
            return;
          }

          if (tempStack.features.filter(e => {
            let matches = 0;
            selectedgraticle[0].geometry.coordinates[0].forEach(selected => {
              e.geometry.coordinates[0].forEach(compareTo => {
                if ((compareTo[0] === selected[0]) && (compareTo[1] === selected[1])) {
                  matches++;
                  return;
                }
              })
            });
            return matches > 1;
          }).length !== 0) {
            tempStack.features.push(selectedgraticle[0])
            setSelectedGraticuleStack(tempStack)
            return;
          } else {
            setAlerts(alerts => [
              ...alerts,
              {
                id: new Date().getTime(),
                type: 'danger',
                headline: `Selected Graticules must have shared sides`,
                message: 'Each graticule selected for a section 40E permit have a shared side'
              }
            ]);
          }
        } else {
          setAlerts(alerts => [
            ...alerts,
            {
              id: new Date().getTime(),
              type: 'danger',
              headline: `Maxiumum selected Graticules Reached (10)`,
              message: 'To select more, please de-select existing Graticules'
            }
          ]);
        }
        return;
      } else {
        setAlerts(alerts => [
          ...alerts,
          {
            id: new Date().getTime(),
            type: 'danger',
            headline: `Please select a Graticule within the selected Exploration Licence`,
            message: 'If you wish to close the Generator, press Cancel in the sidebar. '
          }
        ]);
        return;
      }
    }

    // Check if there is a marker there.
    //
    // Hit-test the RENDERED pin rather than a radius around its coordinate.
    // The pin is anchored at its tip, so the coordinate sits at the bottom
    // point while the part people aim at is the head ~30px above it - and a
    // radius big enough to cover the head also swallows clicks on everything
    // near it. queryRenderedFeatures tests the icon actually on screen, so
    // clicking the pin you can see always hits, and clicking beside it never
    // does. It also scales correctly with zoom, which a pixel radius does not.
    const pinHits = event.target.getLayer(WAYPOINT_PIN_LAYER)
      ? event.target.queryRenderedFeatures(event.point, { layers: [WAYPOINT_PIN_LAYER] })
      : [];

    if (pinHits.length !== 0) {
      // MapLibre drops the UUID string ids TerraDraw uses, so the id rides in
      // properties as `gfid` (see toFc in DrawControl).
      const hit = pinHits[0];
      const [hitLng, hitLat] = hit.geometry.coordinates;
      const id = hit.properties?.gfid;

      // Prefer TerraDraw's own copy when it is running, since that is what
      // edits are applied to. getTerraDrawInstance() is undefined until the
      // control has been added to the map and again after teardown - an
      // unguarded call here threw INSIDE the click handler, which killed the
      // whole click, so nothing on the map could be selected at all.
      const fromDraw = (drawCtrl.getTerraDrawInstance()?.getSnapshot() || [])
        .find(f => f.id === id);

      const feature = fromDraw || { ...hit, id };
      setShowDrawPointPopup(true);
      setDrawPointPopup(<CreateDrawPointPopup drawctrl={drawCtrl} feature={feature} latLng={{ lng: hitLng, lat: hitLat }} setClosed={setShowDrawPointPopup} />)
      return;
    }

    // Collars come from the vector tile source now. Their collar_id is the MVT
    // feature id, not a property - GeoServer uses the view's unique index as the
    // feature id and omits it from the attributes.
    //
    // The popup therefore has to come from the COLLAR overlay, not from
    // DrillHoleOverlay: that one reads one assay interval's fields, which a
    // collar feature does not have, and reading them threw inside render - so
    // the click silently did nothing. The interval popup is still correct for
    // the global search, which returns interval features.
    const selectedDh = event.features.filter(e => e.source === DH_TILE_SOURCE)
    if (selectedDh.length !== 0) {
      setShowDrillHolePopup(true)
      Events.featureInspected('drill_hole');
      import('./drill-hole/DrillHoleOverlayOptimized').then(module => {
        setDrillHolePopup(module.createSingleCollarPopupTable(
          selectedDh[0], event.lngLat, setShowDrillHolePopup,
          setReportDhModalShow, setReportedDh, drillHolefilters.maxDepth))
      })
      return;
    } else {
      setShowDrillHolePopup(false)
      setDrillHolePopup(<></>)
    }

    // Soils come from the vector tile source now; 'soilsamples' is still the
    // GeoJSON source behind A-Number results, so both are click targets.
    const selectedAssays = event.features.filter(
      e => e.source === SOIL_TILE_SOURCE || e.source === 'soilsamples')
    if (selectedAssays.length !== 0) {
      setShowAssayPopup(true)
      Events.featureInspected('soil_assay');
      // The map draws from the deduplicated display view, whose rows carry only
      // what rendering needs. Ask the base layer for the authoritative row
      // before building the popup, so load date, the reported-issue flags and
      // any correction applied since the view was refreshed are all current.
      // Falls back to the clicked feature if that lookup fails - a degraded
      // popup beats no popup.
      import('./soil-assay/AssayOverlay').then(module => {
        const clicked = selectedAssays[0];
        const show = (feature) => setAssayPopup(
          module.createSingleAssayPopupTable(feature, event.lngLat, setShowAssayPopup, setReportAssayModalShow, setReportedAssay)
        );
        show(clicked);
        const id = clicked?.properties?.id;
        if (id === undefined || id === null) return;
        module.queryAssayById(id)
          .then(res => {
            const full = res?.data?.features?.[0];
            if (!full) return;
            // Keep samples_here from the display feature - it is the count of
            // samples at this location and does not exist on the base layer.
            full.properties = { ...full.properties, samples_here: clicked.properties.samples_here };
            show(full);
          })
          .catch(err => console.warn('assay detail lookup failed', err));
      })
      return;
    } else {
      setShowAssayPopup(false)
      setAssayPopup(<></>)
    }

    const selectedminedex = event.features.filter(e => e.source === 'minedex')
    if (selectedminedex.length !== 0) {
      // Clear tenement selections when clicking minedex
      setShowTenementPopup(false)
      setSelectedTenement({ properties: { gid: 99999999 } })
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })

      setShowMinedexPopup(true);
      Events.featureInspected('minedex');
      import('./minedex/MinedexOverlay').then(module => {
        setMinedexPopup(module.createSingleMinedexPopupTable(selectedminedex[0], event.lngLat, setShowMinedexPopup))
      })
      return
    } else {
      setShowMinedexPopup(false)
      setMinedexPopup(<></>)
    }

    // Check for amalgamations pending tenements first (they render on top)
    const selectedAmalgamations = event.features.filter(e => e.source === 'amalgamations-pending')
    if (selectedAmalgamations.length !== 0) {
      // Toggle selection if clicking the same amalgamation
      if (selectedAmalgamation.properties.OBJECTID === selectedAmalgamations[0].properties.OBJECTID) {
        setShowTenementPopup(false)
        setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
        setTenementPopup(<></>)
        return;
      }

      // Clear the old popup first to ensure React re-renders when we set the new one
      setTenementPopup(<></>)
      setSelectedAmalgamation(selectedAmalgamations[0])
      // Clear regular tenement selection
      setSelectedTenement({ properties: { gid: 99999999 } })
      import('./tenements/AmalgamationsPendingOverlay').then(module => {
        setTenementPopup(module.createAmalgamationsPendingPopup(selectedAmalgamations[0], event.lngLat, setShowTenementPopup))
        setShowTenementPopup(true); Events.featureInspected('tenement')
      })
      return;
    }

    // Filter tenements and deduplicate by fmt_tenid (same tenement can appear from multiple layers)
    const allTenementFeatures = event.features.filter(e => e.source === 'tenements')
    const selectedTenements = allTenementFeatures
      .filter((feature, index, self) =>
        index === self.findIndex(f => f.properties.fmt_tenid === feature.properties.fmt_tenid)
      )

    // Debug logging for tenement selection issues
    const clickedIds = selectedTenements.map(f => f.properties.fmt_tenid).join(', ') || 'none'
    const clickedLayers = allTenementFeatures.map(f => f.layer?.id).join(', ') || 'none'
    console.log(`[Tenement Click] clicked: ${clickedIds} | layers: ${clickedLayers} | currentSelected: ${selectedTenement?.properties?.fmt_tenid || 'none'} (gid: ${selectedTenement?.properties?.gid})`)

    if (selectedTenements.length === 1) {
      const actualTenement = tenementGeoJson.features.filter(e => e.properties.fmt_tenid === selectedTenements[0].properties.fmt_tenid)

      // Safety check
      if (!actualTenement || actualTenement.length === 0) {
        console.warn('[Tenement Click] NOT FOUND in geoJson:', selectedTenements[0].properties.fmt_tenid)
        return;
      }

      // Toggle selection if clicking the same tenement
      if (selectedTenement.properties.gid === actualTenement[0].properties.gid) {
        console.log('[Tenement Click] TOGGLE OFF:', actualTenement[0].properties.fmt_tenid)
        setShowTenementPopup(false)
        setSelectedTenement({ properties: { gid: 99999999 } })
        setTenementPopup(<></>)
        return;
      }

      console.log('[Tenement Click] SELECT:', actualTenement[0].properties.fmt_tenid)
      // Clear the old popup first to ensure React re-renders when we set the new one
      setTenementPopup(<></>)
      setSelectedTenement(actualTenement[0])
      // Clear amalgamation selection
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
      // Import createSingleTenementPopupTable from tenements overlay
      import('./tenements/TenementOverlay').then(module => {
        setTenementPopup(module.createSingleTenementPopupTable(actualTenement[0], event.lngLat, setShowTenementPopup, trigger40EWizard))
        setShowTenementPopup(true); Events.featureInspected('tenement')
      })
      return;
    } else if (selectedTenements.length > 1) {
      console.log('[Tenement Click] MULTI-SELECT popup for:', selectedTenements.map(f => f.properties.fmt_tenid).join(', '))
      setShowTenementPopup(true); Events.featureInspected('tenement')
      // Clear amalgamation selection
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
      setTenementPopup(<Popup latitude={event.lngLat.lat} longitude={event.lngLat.lng}
        anchor="bottom"
        onClose={() => {
          setShowTenementPopup(false)
          setSelectedTenement({ properties: { gid: 99999999 } })
        }}>{
          selectedTenements.map((feature) => {
            const actualTenement = tenementGeoJson.features.filter(e => e.properties.fmt_tenid === feature.properties.fmt_tenid)
            // Safety check - skip if tenement not found in geoJson
            if (!actualTenement || actualTenement.length === 0) {
              return null
            }
            return <p key={feature.properties.fmt_tenid}><a href="#" onClick={() => {
              setSelectedTenement(actualTenement[0])
              // Clear amalgamation selection when clicking multiple tenements list
              setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
              import('./tenements/TenementOverlay').then(module => {
                setTenementPopup(module.createSingleTenementPopupTable(actualTenement[0], event.lngLat, setShowTenementPopup, trigger40EWizard))
              })
            }}>
              {actualTenement[0].properties.fmt_tenid}
            </a></p>
          }).filter(Boolean)
        }
      </Popup>)
      return;
    }

    // If we reach here, user clicked on empty space - clear all tenement selections
    if (showTenementPopup && selectedTenements.length === 0) {
      console.log('[Tenement Click Debug] Clearing selection - no tenements detected at click location')
      setShowTenementPopup(false)
      setSelectedTenement({ properties: { gid: 99999999 } })
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } })
      setTenementPopup(<></>)
    }

    const selectedgraticle = event.features.filter(e => e.source === 'graticle')
    if (selectedgraticle.length !== 0) {
      setShowGraticlePopup(true);
      Events.featureInspected('graticule');
      import('./graticles/GraticlesOverlay').then(module => {
        setGraticlePopup(module.createSingleGraticlePopupTable(selectedgraticle[0], event.lngLat, setShowGraticlePopup))
      })
    } else {
      setShowGraticlePopup(false)
      setGraticlePopup(<></>)
    }

    const selectedsection19 = event.features.filter(e => e.source === 'section19')
    if (selectedsection19.length !== 0) {
      setShowSection19Popup(true);
      Events.featureInspected('section19');
      import('./Section19/Section19Overlay').then(module => {
        setSection19Popup(module.createSingleSection19PopupTable(selectedsection19[0], event.lngLat, setShowSection19Popup))
      })
    } else {
      setShowSection19Popup(false)
      setSection19Popup(<></>)
    }

    // "What rock am I on?" - the geology base maps are flat rasters, so a click
    // has to go back to GSWA to name the unit. Only worth doing when a geology
    // layer is actually showing, or when the user clicked a mapped fault.
    // The fault comes from the clicked vector-tile feature itself (the tiles
    // carry feature, type and name), so no second request is needed for it.
    const clickedFault = event.features.find(e => String(e.source || '').startsWith('wa-faults-'));
    const geologyShowing = layerVisibility.geo100 || layerVisibility.geo200
        || (SHOW_INTERPRETED_BEDROCK && layerVisibility.bedrock500);
    if (clickedFault || geologyShowing) {
      Events.featureInspected('geology');
      const fault = clickedFault
        ? { ...clickedFault.properties, scale: faultScale(clickedFault) }
        : null;
      identifyWaGeology(event.lngLat).catch(() => null).then((unit) => {
        if (!unit && !fault) { setShowGeologyPopup(false); return; }
        setShowGeologyPopup(true);
        setGeologyPopup(createWaGeologyPopup(unit, fault, event.lngLat, setShowGeologyPopup));
      });
    } else {
      setShowGeologyPopup(false)
      setGeologyPopup(<></>)
    }
    // landChange.visible and queryLandChange belong here: without them the
    // handler closes over the value from whenever it was last rebuilt, so
    // turning the layer on did nothing until some unrelated dependency
    // changed -- which is why the popup only appeared sometimes.
  }, [is40ESideBarShown, selectedGraticleStack, tenementGeoJson, tenement40E, drawCtrl, selectedTenement, showTenementPopup, selectedAmalgamation, layerVisibility, landChange.visible, queryLandChange, drillHolefilters.maxDepth]);

  // Ask the catalogue what exists over this viewport. Skipped entirely (no
  // network) unless the dated layer is on and we are above its zoom gate.
  //
  // This runs for ANY base layer once we are zoomed into a real area, because
  // the age chip's whole job on the other layers is to say "there is imagery of
  // this spot from 11 days ago". It is one keyless ~0.6s request per new
  // viewport, and the rounded-bbox LRU absorbs the rest.
  const sentinelEnabled = !!layerVisibility.sentinel2Dated;
  const imagerySensor = SCAN_SENSORS[imageryPrefs.sensor] || SCAN_SENSORS.s2;
  const imageryRange = DATE_RANGES.find(r => r.id === imageryPrefs.rangeId) || DATE_RANGES[1];
  const {
    dates: sentinelDates,
    latestClear,
    loading: sentinelLoading,
  } = useSentinelDates({
    bbox: viewBbox,
    enabled: mapZoom === null || mapZoom >= SENTINEL_MIN_ZOOM,
    maxCloud: imageryPrefs.maxCloud,
    // Only the dated layer needs the deep archive; the chip just needs the
    // newest clear scene, so other layers stay on the cheap 6-month window.
    lookbackDays: sentinelEnabled ? imageryRange.days : 183,
    collection: imagerySensor.collection,
    archiveStart: `${imagerySensor.archiveStart}T00:00:00Z`,
    // Jumping to an old year from a land-change popup must actually reach it.
    focusDate: sentinelEnabled ? imageryPrefs.date : null,
  });

  // A remembered date only holds while it exists over the current view.
  // Otherwise snap to the nearest available and SAY so — silently showing a
  // different date than the one on the label is the failure to avoid.
  const resolvedDate = useMemo(() => {
    if (!sentinelEnabled) return { date: null, note: null };
    const usable = sentinelDates.filter(
      d => d.cloud === null || d.cloud <= imageryPrefs.maxCloud
    );
    if (!imageryPrefs.date) return { date: latestClear?.date ?? null, note: null };
    if (sentinelDates.some(d => d.date === imageryPrefs.date)) {
      return { date: imageryPrefs.date, note: null };
    }
    if (!usable.length) return { date: null, note: null };
    const wanted = new Date(imageryPrefs.date).getTime();
    let nearest = usable[0];
    let gap = Infinity;
    for (const d of usable) {
      const g = Math.abs(new Date(d.date).getTime() - wanted);
      if (g < gap) { gap = g; nearest = d; }
    }
    return { date: nearest.date, note: `nearest to ${imageryPrefs.date}` };
  }, [sentinelEnabled, sentinelDates, imageryPrefs.date, imageryPrefs.maxCloud, latestClear]);

  // Compare mode: two dates, defaulting to the ends of what is clear here so
  // the layer says something useful the moment it is switched on.
  const clearDates = useMemo(
    () => sentinelDates.filter(d => d.cloud === null || d.cloud <= imageryPrefs.maxCloud),
    [sentinelDates, imageryPrefs.maxCloud]
  );
  const targetSentinelDate = resolvedDate.date;
  const selectedScene = useMemo(
    () => sentinelDates.find(d => d.date === targetSentinelDate) || null,
    [sentinelDates, targetSentinelDate]
  );

  useEffect(() => { setImagerySnapNote(resolvedDate.note); }, [resolvedDate.note]);

  const updateImageryPrefs = useCallback(patch => {
    setImageryPrefs(prev => {
      const next = { ...prev, ...patch };
      saveImageryPrefs(next);
      return next;
    });
  }, []);

  const handleSelectImageryDate = useCallback(
    date => {
      updateImageryPrefs({ date });
      if (date === null) setImagerySnapNote(null);
    },
    [updateImageryPrefs]
  );

  useEffect(() => { if (!sentinelEnabled && imageryPlaying) setImageryPlaying(false); },
    [sentinelEnabled, imageryPlaying]);

  /**
   * Take the user to the imagery from when a change was detected.
   *
   * The stored year is the first FULL year at the reduced level, so the work
   * usually happened shortly before it — mid-year is the closest single guess.
   * The exact date does not need to exist: the resolver already snaps to the
   * nearest available acquisition and says so in the time bar.
   */
  const showImageryForYear = useCallback((year, sensorId = 's2') => {
    setLayerVisibility(prev => {
      const next = {};
      for (const key in prev) next[key] = key === 'sentinel2Dated';
      return next;
    });
    // The whole point of this jump is to compare the imagery against the
    // detected change, so the change layer comes with you whether or not it
    // happened to be on. Its own anchor keeps it above the imagery.
    setLandChange(prev => ({ ...prev, visible: true }));
    updateImageryPrefs({
      sensor: sensorId,
      // Style ids differ between the two layers, so carry the choice across
      // only when the new sensor actually has it.
      styleId: styleForSensor(sensorId, imageryPrefs.styleId),
      date: `${year}-07-01`,
      rangeId: 'all',
      maxCloud: Math.max(imageryPrefs.maxCloud, 20),
    });
    setLandChangePopup(null);
  }, [updateImageryPrefs, imageryPrefs.styleId, imageryPrefs.maxCloud]);

  const showSentinelLayer = useCallback(() => {
    setLayerVisibility(prev => {
      const next = {};
      for (const key in prev) next[key] = key === 'sentinel2Dated';
      return next;
    });
  }, []);

  // Load the incoming date into whichever buffer is NOT on screen. The style is
  // part of the identity: switching true colour -> NDVI is as much a reload as
  // switching date, and deserves the same cross-fade rather than a blank flash.
  const sentinelTarget = targetSentinelDate
    ? `${imagerySensor.id}|${targetSentinelDate}|${imageryPrefs.styleId}`
    : null;

  useEffect(() => {
    if (!sentinelTarget) return;
    if (s2BufferDates[s2Active] === sentinelTarget) return;
    const idle = s2Active === 'a' ? 'b' : 'a';
    if (s2BufferDates[idle] === sentinelTarget) {
      // Already loaded there (e.g. flipping back) — just show it.
      setS2Active(idle);
      setS2Pending(null);
      return;
    }
    setS2BufferDates(prev => ({ ...prev, [idle]: sentinelTarget }));
    setS2Pending(idle);
  }, [sentinelTarget, s2Active, s2BufferDates]);

  // Only swap once the incoming buffer actually has tiles, so the fade happens
  // between two complete pictures rather than to a white gap.
  const handleSourceData = useCallback(
    e => {
      if (!s2Pending) return;
      if (e.sourceId === `sentinel-${s2Pending}` && e.isSourceLoaded) {
        setS2Active(s2Pending);
        setS2Pending(null);
      }
    },
    [s2Pending]
  );

  // Optimized handleMapMoveEnd with debouncing and caching
  const handleMapMoveEnd = useCallback(debounce((e) => {
    const bounds = e.target.getBounds();
    const viewBox = [
      bounds.getSouthWest().lng, bounds.getSouthWest().lat,
      bounds.getNorthEast().lng, bounds.getNorthEast().lat,
    ];

    // Fetch a margin around the viewport rather than exactly the viewport, so
    // panning shows data that is already loaded instead of blank ground that
    // fills in a moment later. Doubling each dimension covers 4x the area for
    // about +83% payload - density falls off outside the cluster you are
    // looking at - and the containment check below turns that into far fewer
    // requests, not just bigger ones.
    const wfsbbox = expandBbox(viewBox, WFS_FETCH_FACTOR).join();
    const zoom = e.target.getZoom();

    /**
     * Has this layer already fetched ground covering the current viewport,
     * under the same filters? Compares against the EXPANDED box that was
     * fetched, not the viewport it was fetched for.
     */
    const dispatched = [];
    const needsFetch = (key, filterSig) => {
      const prev = fetchedRef.current[key];
      if (prev && prev.sig === filterSig && bboxContains(prev.bbox, viewBox)) return false;
      // Only this layer is superseded, so only this layer's in-flight request
      // is abandoned. Cancelling the others would strand them: see the note
      // above about why nothing is cancelled up front.
      //
      // The abort controllers are registered under the query modules' own
      // names, which are not these keys - 'dh' here is 'drillholes' there.
      // Passing the wrong name is a silent no-op, so the mapping is explicit.
      // minedex and graticules register no controller at all.
      const CONTROLLER_KEYS = { tenements: 'tenements', dh: 'drillholes', soils: 'assays' };
      const controllerKey = CONTROLLER_KEYS[key];
      if (controllerKey) requestManager.cancelRequest(controllerKey);
      fetchedRef.current[key] = { bbox: wfsbbox, sig: filterSig };
      dispatched.push(key);
      return true;
    };
    const center = e.target.getCenter();

    // Save viewport for persistence (restore on next visit)
    saveViewport(center.lat, center.lng, zoom);

    // map_view, rate-limited. moveend fires on every pan; sending one event
    // each would burn the GA4 per-session event cap on noise. Only a change of
    // zoom BAND (state -> region -> prospect -> detail) is interesting, and
    // then at most once a minute. Centre coordinates are deliberately not
    // reported — where a user is looking is their prospecting intent.
    const nowTs = Date.now();
    const band = zoom < 8 ? 'state' : zoom < 10.6 ? 'region' : zoom < 13 ? 'district' : zoom < 16 ? 'prospect' : 'detail';
    if (band !== lastMapViewRef.current.band && nowTs - lastMapViewRef.current.at > 60000) {
      lastMapViewRef.current = { band, at: nowTs };
      Events.mapView(zoom, activeBaseMap());
    }

    // Feed the Sentinel-2 date catalogue (rounded + cached inside the hook)
    setViewBbox([
      bounds.getSouthWest().lng,
      bounds.getSouthWest().lat,
      bounds.getNorthEast().lng,
      bounds.getNorthEast().lat,
    ]);
    setMapZoom(zoom);

    // Deliberately NOT cancelAllRequests() here.
    //
    // This used to cancel everything in flight before deciding what to fetch,
    // which combined with the coverage guard below to strand layers. A pan
    // dispatches a fetch and records the ground it covers; a second pan 400 ms
    // later aborted that still-in-flight request, and then needsFetch saw the
    // new viewport as already covered and scheduled nothing to replace it. The
    // request died, the guard believed the ground was loaded, and the layer
    // stayed empty until the user panned clear of the recorded box - which
    // looks exactly like "tenements only render where the map was refreshed".
    //
    // Each layer's request is cancelled only when that same layer is about to
    // re-issue, which is what the abort was for in the first place. An
    // in-flight fetch for a layer we are NOT refetching is left alone to
    // finish and paint.

    // Batch all queries together to avoid multiple renders
    const queries = [];

    // Soils are NOT fetched here any more. They are served as vector tiles, so
    // MapLibre requests what the viewport needs and caches it - no bbox
    // tracking, no debounce, no abort controller, and panning costs the
    // database nothing. The only soil WFS left is the A-Number lookup, which
    // is a whole-of-report query with no bbox and is issued from the sidebar.

    if (overlayVisibility.tenements && zoom > 8 && needsFetch('tenements', JSON.stringify(tenementFilters))) {
      queries.push(
        import('./tenements/TenementOverlayOptimized').then(module =>
          module.debouncedQueryTenementWFS(wfsbbox, tenementFilters, setTenementGeojson)
        )
      );
    }

    // MINEDEX is NOT fetched here any more - vector tiles, same as soils and
    // drill collars. The WFS returned the whole visible set as GeoJSON: 3.59 MB
    // at the zoom the layer first appeared, and again on every pan and every
    // filter change. Tiles cover the same viewport in 0.65 MB, are cached, and
    // move filtering into a style expression with no round trip at all.

    // Drill holes are NOT fetched here any more - vector tiles, same as soils.
    // The WFS returned one row per assay INTERVAL, 14.9 per collar, all at the
    // same coordinate; the tile layer is one row per collar.

    if (overlayVisibility.graticles && needsFetch('graticles', '-')) {
      queries.push(
        import('./graticles/GraticlesOverlay').then(module =>
          module.queryGraticlesWFSLibre(wfsbbox, setGraticleGeoJson)
        )
      );
    }

    // Execute all queries concurrently
    Promise.allSettled(queries).then(results => {
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.warn(`Query ${index} failed:`, result.reason);
          // needsFetch records the fetch when it DISPATCHES, so a failure has
          // to undo that - otherwise the layer is remembered as covered and
          // panning inside the box never retries, leaving it permanently blank.
          const key = dispatched[index];
          if (key) delete fetchedRef.current[key];
        }
      });
    });

  }, 250), [assayFilters, overlayVisibility, tenementFilters, minedexFilters, drillHolefilters, is40ESideBarShown, saveViewport]);

  // Keep existing utility functions
  const createSquareImg = (r, g, b, w) => {
    const width = w;
    const bytesPerPixel = 4;
    const data = new Uint8Array(width * width * bytesPerPixel);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < width; y++) {
        const offset = (y * width + x) * bytesPerPixel;
        data[offset + 0] = r;
        data[offset + 1] = g;
        data[offset + 2] = b;
        data[offset + 3] = 255;
      }
    }
    return { width, height: width, data };
  };

  const createTriangleImg = (r, g, b, w) => {
    const width = w;
    const bytesPerPixel = 4;
    const data = new Uint8Array(width * width * bytesPerPixel);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < width; y++) {
        const offset = (y * width + x) * bytesPerPixel;
        data[offset + 0] = r;
        data[offset + 1] = g;
        data[offset + 2] = b;
        data[offset + 3] = Math.abs(x - width) <= y ? 255 : 0;
      }
    }
    return { width, height: width, data };
  };

  // Keep existing map load handler
  const handleOnLoad = useCallback(e => {
    // Keep existing implementation
    // All eleven MINEDEX symbols, drawn to match the DMIRS legend so a site
    // looks the same here as it does in GeoVIEW. See minedex/dmirsSymbols.js.
    addMinedexSymbols(e.target);

    
    setMapController(createMapLibreGlMapController(e.target, maplibregl));

    // Seed the imagery viewport — moveEnd has not fired yet on a fresh load.
    const bounds = e.target.getBounds();
    setViewBbox([
      bounds.getSouthWest().lng,
      bounds.getSouthWest().lat,
      bounds.getNorthEast().lng,
      bounds.getNorthEast().lat,
    ]);
    setMapZoom(e.target.getZoom());
  }, []);

  /**
   * What happens after you choose a search result.
   *
   * Flying to the extent is not enough for an A-Number. The sidebar lookup this
   * replaces showed EVERY sample in the report; just moving the camera leaves
   * the normal viewport query in charge, so the PPB/g-t threshold quietly hides
   * the low-grade samples - and a whole report is exactly the thing you want to
   * see unfiltered. runSearch already fetched the features to work out the
   * extent, so they are handed straight to the layer rather than re-queried.
   */
  const handleSearchPick = useCallback((result) => {
    Events.featureInspected(`search_${result.source}`);

    if (result.source === 'anumberSoil' && result.features?.length) {
      setAssayGeojson({ type: 'FeatureCollection', features: result.features });
      setAssayFilters(f => ({ ...f, anumber: Number(String(result.title).replace(/\D/g, '')) }));
      setOverlayVisisbility(v => ({ ...v, soils: true }));
    }

    if (result.source === 'anumberDrill' && result.features?.length) {
      setDrillHoleGeoJson({ type: 'FeatureCollection', features: result.features });
      setOverlayVisisbility(v => ({ ...v, dh: true }));
    }

    // Turning the layer on is the difference between "the map moved" and "I can
    // see the thing I searched for" - a MINEDEX hit is invisible if the MINEDEX
    // overlay happens to be off.
    if (result.source === 'minedexSite') {
      setOverlayVisisbility(v => ({ ...v, minedex: true }));
    }
  }, []);

  /**
   * Opens the matching popup once the camera has settled on a search result.
   *
   * Flying to a feature and then making you click it is a wasted step - you
   * already told the app which one you wanted. Only single features get this:
   * an A-Number is a whole report, so there is nothing single to open, and a
   * place is not one of ours.
   *
   * Each popup is built exactly as onMapClick builds it, including the report
   * modal setters, so a feature reached by search behaves identically to one
   * reached by clicking - same actions, same 40E wizard hook.
   */
  const handleSearchArrive = useCallback((result) => {
    const at = result.lngLat;
    if (!at) return;

    if (result.source === 'tenement' && result.feature) {
      setSelectedTenement(result.feature);
      setSelectedAmalgamation({ properties: { OBJECTID: 99999999 } });
      import('./tenements/TenementOverlay').then(module => {
        setTenementPopup(module.createSingleTenementPopupTable(result.feature, at, setShowTenementPopup, trigger40EWizard));
        setShowTenementPopup(true);
      });
      return;
    }

    if (result.source === 'minedexSite' && result.feature) {
      import('./minedex/MinedexOverlay').then(module => {
        setMinedexPopup(module.createSingleMinedexPopupTable(result.feature, at, setShowMinedexPopup));
        setShowMinedexPopup(true);
      });
      return;
    }

    if (result.source === 'soilSample' && result.feature) {
      import('./soil-assay/AssayOverlay').then(module => {
        setAssayPopup(module.createSingleAssayPopupTable(result.feature, at, setShowAssayPopup, setReportAssayModalShow, setReportedAssay));
        setShowAssayPopup(true);
      });
      return;
    }

    if (result.source === 'drillHole' && result.feature) {
      import('./drill-hole/DrillHoleOverlay').then(module => {
        setDrillHolePopup(module.createSingleDrillHolePopupTable(result.feature, at, setShowDrillHolePopup, setReportDhModalShow, setReportedDh));
        setShowDrillHolePopup(true);
      });
    }
  }, [trigger40EWizard]);

  /**
   * The map cursor, in one place.
   *
   * MapLibre's stylesheet puts a grab-hand on the canvas by default, which is
   * a panning affordance the app does not need - the map is read far more than
   * it is dragged, and an arrow is what people expect over a document.
   *
   * Two owners would fight here: react-map-gl writes this prop onto the canvas
   * as an inline style, and DrawControl used to write `crosshair` onto the same
   * canvas directly. Whichever ran last won, and mouse-move runs constantly, so
   * the crosshair lost. Draw mode is therefore resolved here instead.
   */
  const mapCursor = drawMode ? 'crosshair' : (hoveringPoint ? 'pointer' : 'default');

  // Keep existing mouse move handler
  const handleMouseMove = useCallback(e => {
    const point = utm.convertLatLngToUtm(e.lngLat.lat, e.lngLat.lng, 2);
    setMousePoint(`${point.ZoneNumber}${point.ZoneLetter} ${point.Easting}mE ${point.Northing}mN`);
    // Kept alongside the MGA string so the readout can switch format without
    // re-projecting anything on click.
    setMouseLngLat({ lat: e.lngLat.lat, lng: e.lngLat.lng });

    // Pointer only over the things that behave like buttons - a sample, a hole,
    // a MINEDEX site, a waypoint. Deliberately NOT tenements, graticules or
    // faults: those cover most of the screen most of the time, so a pointer
    // over them would just be the old hand cursor by another name.
    // `-dh` catches the per-colour drill-hole layers, whose ids are generated
    // from the marker colour settings and so cannot be listed up front.
    const over = (e.features || []).some(f => {
      const id = f.layer?.id;
      return !!id && (POINT_HOVER_LAYERS.has(id) || id.endsWith('-dh'));
    });
    setHoveringPoint(prev => (prev === over ? prev : over));
  }, []);


  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      requestManager.cancelAllRequests();
      wfsCache.clear();
    };
  }, []);

  // Show loading state while restoring viewport
  if (isViewportLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading map...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <MapProvider>
        <Collapse in={mobileFiltersOpen}>
          <div id="mobile-filter-collapse" className='border-bottom' style={{
            fontSize: 12,
            maxHeight: '60vh',
            overflowY: 'auto',
            backgroundColor: 'white',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1050,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <SideBar
              setTenementFilters={setTenementFilters}
              tenementFilters={tenementFilters}
              layerVisibility={layerVisibility}
              setLayerVisibility={setLayerVisibility}
              asterVisibility={asterVisibility}
              setAsterVisibility={setAsterVisibility}
              asterOpacity={asterOpacity}
              setAsterOpacity={setAsterOpacity}
              deaVisibility={deaVisibility}
              setDeaVisibility={setDeaVisibility}
              deaOpacity={deaOpacity}
              setDeaOpacity={setDeaOpacity}
              deaOptions={deaOptions}
              setDeaOptions={setDeaOptions}
              landChange={landChange}
              setLandChange={setLandChange}
              terrain={terrain}
              setTerrain={setTerrain}
              overlayVisibility={overlayVisibility}
              setOverlayVisisbility={setOverlayVisisbility}
              setTenementPopup={setTenementPopup}
              setShowTenementPopup={setShowTenementPopup}
              assayMarkerColourSettings={assayMarkerColourSettings}
              setAssayMarkerColourSettings={setAssayMarkerColourSettings}
              assayFilters={assayFilters}
              setAssayFilters={setAssayFilters}
              assayGeoJson={assayGeoJson}
              setAssayGeoJson={setAssayGeojson}
              minedexFilters={minedexFilters}
              setMinedexFilters={setMinedexFilters}
              drillHoleFilters={drillHolefilters}
              setDrillHoleFilters={setDrillHoleFilters}
              drillHoleColourSettings={drillHoleMarkerColourSettings}
              setDrilHoleColourSettings={setDrillHoleMarkerColourSettings}
              drillHoleGeoJson={drillHoleGeoJson}
              prospectFilters={prospectFilters}
              setProspectFilters={setProspectFilters}
              trigger40EWizard={trigger40EWizard}
              drawCtrl={drawCtrl}
                waypointRevision={waypointRevision}
            />
          </div>
        </Collapse>

        <div className='goldfinder-libre-map'>
            <AlertList onDismiss={onDismissed} alerts={alerts} position="bottom-left" timeout={2000}
              classes={{
                close: classNames("btn-close", "no-text"),
                dismissable: classNames("d-flex", "align-items-start", "flex-row-reverse")
              }}
            />

            <Map
              onLoad={handleOnLoad}
              onMoveEnd={handleMapMoveEnd}
              onSourceData={handleSourceData}
              id='goldfindermap'
              initialViewState={initialViewState}
              mapStyle={goldfinderstyle}
              className=""
              cursor={mapCursor}
              onMouseMove={handleMouseMove}
              onClick={onMapClick}
              attributionControl={false}
              interactiveLayerIds={[
                ...WA_FAULT_LAYER_IDS,
                'live-tenements',
                'pending-tenements',
                'selected-tenement-fill',
                'amalgamations-pending-fill',
                SOIL_TILE_BACKGROUND_LAYER,
                'assay-anumber-config',
                // Generated from the DMIRS symbol table, not hardcoded. The
                // old literals here ('alumina-layer', 'iron-layer', ...) were
                // left behind when the layers were renamed, so every MINEDEX
                // site silently stopped responding to clicks - the layer has
                // to be in this list to appear in event.features at all.
                ...MINEDEX_LAYER_IDS,
                'graticle-fill',
                'section19-fill',
                'dead-tenements',
                'dead-spl-tenements',
                DH_TILE_BACKGROUND_LAYER,
                WAYPOINT_PIN_LAYER,
                ...drillHoleMarkerColourSettings.map(setting => `${setting.id}-dh`)
              ]}
            >
              {/* Camera controls, in this order: the CSS in map-controls.css
                  welds these two groups into one pill by nth-of-type, so
                  inserting another top-left control here needs that checked. */}
              <GeolocateControl trackUserLocation={true} positionOptions={{ enableHighAccuracy: true, timeout: 2000 }} position="top-left" />
              <NavigationControl position="top-left" />
              {/* Fullscreen is deliberately absent. It is a once-a-session view
                  command that F11 already covers, and as a permanent button it
                  cost a slot in the rail and broke the grouping of the controls
                  around it. Re-add as <FullscreenControl position="top-right" />
                  if it is ever wanted back - map-controls.css already positions
                  the top-right container under the layers toggle. */}
              <DrawControl drawCtrl={drawCtrl} drawpointMode={drawpointMode} overlayVisibility={overlayVisibility} onModeChange={setDrawMode} onStoreChange={bumpWaypoints} />
              <ScaleControl />

              {/* Base layers */}
              {/* Ordering anchors.
                  MapLibre draws in insertion order, and react-map-gl appends a
                  layer when its <Source> MOUNTS -- so any conditionally
                  rendered base map (the Sentinel buffers, the backdrop,
                  terrain) jumps on top of every overlay the moment it is
                  switched on. Declaring order alone cannot fix that.
                  The two marker layers below are placed at the real boundaries
                  and never unmount, so everything else pins itself with
                  beforeId and keeps its depth across remounts. An anchor is
                  declared here, before anything references them: MapLibre's
                  beforeId requires the target layer to already exist, so a
                  forward reference silently drops the layer entirely. Order is
                  set by beforeId, not by where these sit. */}
              <Source id='layer-anchors' type='geojson' data={{ type: 'FeatureCollection', features: [] }} />
              <Layer id='anchor-base-top' source='layer-anchors' type='circle' paint={{ 'circle-opacity': 0, 'circle-radius': 0 }} />
              <Layer id='anchor-overlay-top' source='layer-anchors' type='circle' paint={{ 'circle-opacity': 0, 'circle-radius': 0 }} />
              <Layer id='anchor-landchange-top' source='layer-anchors' type='circle' paint={{ 'circle-opacity': 0, 'circle-radius': 0 }} />
              {/* Map plates (PlatesOverlay): over the DEA/ASTER rasters, under Land Change.
                  Declared after the anchor it pins below, which must already exist. */}
              <Layer id='anchor-plates-top' source='layer-anchors' type='circle' paint={{ 'circle-opacity': 0, 'circle-radius': 0 }} beforeId='anchor-landchange-top' />

              <Source id='street' type='raster' scheme='xyz' tileSize={256} attribution={"&copy; Google Maps"} tiles={["https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"]}>
                {/* Also the fallback below the imagery's usable zoom range while
                    Sentinel-2 (dated) is selected — otherwise zooming out to
                    state level leaves the map empty. Removed once our own
                    mosaic covers z0 (M6b). */}
                <Layer
                  id='street'
                  source='street'
                  type='raster'
                  maxzoom={!layerVisibility.street && layerVisibility.sentinel2Dated ? BACKDROP_MIN_ZOOM : 24}
                  layout={{ visibility: layerVisibility.street || layerVisibility.sentinel2Dated ? 'visible' : 'none' }} beforeId='anchor-base-top' />
              </Source>
              <Source id='hybrid' type='raster' scheme='xyz' tileSize={256} attribution={"&copy; Google Maps"} tiles={["https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"]}>
                <Layer id='hybrid' layout={{ visibility: `${layerVisibility.hybrid ? "visible" : "none"}` }} source='hybrid' type='raster' beforeId='anchor-base-top' />
              </Source>
              {/* Cloud-free Sentinel-2 backdrop — DEA Barest Earth, CC BY 4.0.
                  Was EOX s2cloudless, which is CC BY-NC-SA 4.0 and so cannot ship in
                  a paid product. Barest Earth is the same idea from a licence we can
                  actually use: a composite picking the least-vegetated, cloud-free
                  observation per pixel, which for prospecting is arguably better —
                  it shows ground rather than canopy. */}
              <Source
                id='sentinel2'
                type='raster'
                scheme='xyz'
                tileSize={256}
                maxzoom={SENTINEL_MAX_ZOOM}
                attribution={DEA_ATTRIBUTION}
                tiles={buildBarestEarthTiles()}
              >
                <Layer id='sentinel2' layout={{ visibility: `${SHOW_BAREST_EARTH && layerVisibility.sentinel2 ? "visible" : "none"}` }} source='sentinel2' type='raster' beforeId='anchor-base-top' />
              </Source>
              {/* Sentinel-2 (dated) — Digital Earth Australia, CC BY 4.0.
                  Two buffers cross-faded by raster-opacity: the incoming date
                  loads underneath at opacity 0 and only becomes visible once
                  its tiles have landed (see handleSourceData). MapLibre
                  animates the paint change, so the swap reads as a dissolve.
                  maxzoom caps at native S2 detail and lets MapLibre overzoom. */}
              {['a', 'b'].map(buf => {
                const [bufSensor, bufDate, bufStyle] = (s2BufferDates[buf] || '').split('|');
                const tiles = buildDatedTiles(bufSensor, bufDate, bufStyle);
                if (!tiles) return null;
                return (
                  <Source
                    key={`sentinel-${buf}`}
                    id={`sentinel-${buf}`}
                    type='raster'
                    scheme='xyz'
                    tileSize={256}
                    maxzoom={SENTINEL_MAX_ZOOM}
                    attribution={DEA_ATTRIBUTION}
                    tiles={tiles}
                  >
                    <Layer
                      id={`sentinel-${buf}`}
                      source={`sentinel-${buf}`}
                      type='raster'
                      beforeId='anchor-base-top'
                      minzoom={SENTINEL_MIN_ZOOM}
                      layout={{ visibility: layerVisibility.sentinel2Dated ? 'visible' : 'none' }}
                      paint={{ 'raster-opacity': s2Active === buf ? 1 : 0 }}
                    />
                  </Source>
                );
              })}

              {landChangePopup && (
                <Popup
                  longitude={landChangePopup.lngLat.lng}
                  latitude={landChangePopup.lngLat.lat}
                  /* Anchored opposite the tenement popup: clicking a polygon
                     inside a tenement opens both, and stacking them at the same
                     point left this one unreadable underneath. */
                  anchor='top'
                  offset={12}
                  maxWidth='320px'
                  onClose={() => setLandChangePopup(null)}
                >
                  <div style={{ font: '13px system-ui, sans-serif' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                      Land change{landChangePopup.features.length > 1
                        ? ` — ${landChangePopup.features.length} events here`
                        : ''}
                      {landChangePopup.features.length > 1
                        && landChangePopup.features.some(f => f.direction === 'gain')
                        && landChangePopup.features.some(f => f.direction !== 'gain') && (
                        <div style={{ fontWeight: 400, fontSize: 11, color: '#2f7a3f', marginTop: 2 }}>
                          Cleared and later greening here — likely regrowth, not a standing clearing.
                        </div>
                      )}
                    </div>
                    {landChangePopup.features.map((p, i) => (
                      <div key={i} style={{ paddingTop: i ? 8 : 0, borderTop: i ? '1px solid #eee' : 'none' }}>
                        <div>
                          <strong>
                            {p.direction === 'gain' ? 'Greener since' : 'Cleared'} {p.loss_year}
                          </strong>
                          <span style={{ color: '#6b6b6b' }}> · {p.years_ago} years ago</span>
                        </div>
                        <div style={{ color: '#4a403a' }}>{Number(p.area_ha).toLocaleString()} ha</div>
                        {/* Recovery only means something for a loss; a greening
                            polygon carries no regrown_fraction to report. */}
                        {p.direction !== 'gain' && (
                          <div style={{ color: '#6b6b6b', fontSize: 12 }}>
                            {p.regrown_fraction > 0.5
                              ? `Mostly regrown${p.recovery_after_years ? ` after ${p.recovery_after_years} years` : ''}`
                              : p.regrown_fraction > 0
                              ? `${Math.round(p.regrown_fraction * 100)}% regrown`
                              : 'Still cleared'}
                          </div>
                        )}
                        {p.drop_pct_points != null && (
                          <div style={{ color: '#8a8a8a', fontSize: 11 }}>
                            green cover {p.direction === 'gain' ? 'rose' : 'fell'} {p.drop_pct_points} points
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                          {p.loss_year >= 2016 && (
                            <button
                              type='button'
                              onClick={() => showImageryForYear(p.loss_year, 's2')}
                              title='10 m Sentinel-2 from the year the change was detected'
                              style={{
                                border: '1px solid #fd7218', background: '#fd7218', color: '#fff',
                                borderRadius: 6, padding: '3px 9px', cursor: 'pointer',
                                font: 'inherit', fontSize: 12,
                              }}
                            >
                              Sentinel-2 {p.loss_year} · 10 m
                            </button>
                          )}
                          {/* Landsat reaches 1986, so every event has imagery from
                              its own year -- and it is the data the detection was
                              actually made from. */}
                          <button
                            type='button'
                            onClick={() => showImageryForYear(p.loss_year, 'landsat')}
                            title='30 m Landsat — the imagery this detection was derived from'
                            style={{
                              border: '1px solid #d6d0c9', background: '#fff', color: '#4a403a',
                              borderRadius: 6, padding: '3px 9px', cursor: 'pointer',
                              font: 'inherit', fontSize: 12,
                            }}
                          >
                            Landsat {p.loss_year} · 30 m
                          </button>
                        </div>
                      </div>
                    ))}
                    <div style={{ color: '#8a8a8a', fontSize: 11, marginTop: 8 }}>
                      Derived from annual Landsat cover, 30 m. The year is the first full
                      year at the reduced level and can lag the work by up to a year.
                    </div>
                  </div>
                </Popup>
              )}

              {/* Undated backdrop for the zoom range below the dated layer.
                  s2_barest_earth is a multi-year cloud-free composite, so it
                  never goes stale — this is tier 1 of the caching plan, served
                  live from DEA until M6b re-points it at our own bucket. */}
              <Source
                id='sentinel-backdrop'
                type='raster'
                scheme='xyz'
                tileSize={256}
                maxzoom={SENTINEL_MAX_ZOOM}
                attribution={DEA_ATTRIBUTION}
                tiles={buildBarestEarthTiles()}
              >
                <Layer
                  id='sentinel-backdrop'
                  source='sentinel-backdrop'
                  type='raster'
                  beforeId='anchor-base-top'
                  minzoom={BACKDROP_MIN_ZOOM}
                  maxzoom={SENTINEL_MIN_ZOOM}
                  layout={{ visibility: layerVisibility.sentinel2Dated ? 'visible' : 'none' }}
                />
              </Source>

              <Source id='topo' type='raster' scheme='xyz' tileSize={256} attribution={'© Commonwealth of Australia (Geoscience Australia) 2020'} tiles={["https://services.ga.gov.au/gis/rest/services/NationalBaseMap/MapServer/tile/{z}/{y}/{x}"]}>
                <Layer id='topo' layout={{ visibility: `${layerVisibility.topo ? "visible" : "none"}` }} source='topo' type='raster' beforeId='anchor-base-top' />
              </Source>
              <Source id='geo100' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["	https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/DMIRS_Imagery_Service/MapServer/WMSServer?service=WMS&request=GetMap&layers=13&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>               
               <Layer id='geo100' layout={{ visibility: `${layerVisibility.geo100 ? "visible" : "none"}` }} source='geo100' type='raster' beforeId='anchor-base-top' />
              </Source>
              <Source id='geo200' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["	https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/DMIRS_Imagery_Service/MapServer/WMSServer?service=WMS&request=GetMap&layers=12&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>                
              <Layer id='geo200' layout={{ visibility: `${layerVisibility.geo200 ? "visible" : "none"}` }} source='geo200' type='raster' beforeId='anchor-base-top' />
              </Source>
              {/* GSWA geology (SLIP). WMS layer numbers here are the REVERSE of the
                  REST ids on the same service - 18 is 1:100k interpreted bedrock,
                  15 is the regolith grid. This previously pointed at 15. */}

              <Source id='bedrock500' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/Geology_and_Soils_Map/MapServer/WMSServer?service=WMS&request=GetMap&layers=16&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>
              <Layer id='bedrock500' layout={{ visibility: `${SHOW_INTERPRETED_BEDROCK && layerVisibility.bedrock500 ? "visible" : "none"}` }} source='bedrock500' type='raster' beforeId='anchor-base-top' />
              </Source>

              <Source id='regolith' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/Geology_and_Soils_Map/MapServer/WMSServer?service=WMS&request=GetMap&layers=15&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>
              <Layer id='regolith' layout={{ visibility: `${layerVisibility.regolith ? "visible" : "none"}` }} source='regolith' type='raster' beforeId='anchor-base-top' />
              </Source>

              <Source id='tectonic' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/Geology_and_Soils_Map/MapServer/WMSServer?service=WMS&request=GetMap&layers=12&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>
              <Layer id='tectonic' layout={{ visibility: `${layerVisibility.tectonic ? "visible" : "none"}` }} source='tectonic' type='raster' beforeId='anchor-base-top' />
              </Source>

              <Source id='magnetic' scheme='tms' tileSize={256} attribution={'© State of Western Australia (Department of Energy, Mines, Industry Regulation and Safety) 2025'} type='raster' tiles={["https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/DMIRS_Imagery_Service/MapServer/WMSServer?service=WMS&request=GetMap&layers=6&styles=&format=image/jpeg&transparent=false&version=1.1.1&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}"]}>
              <Layer id='magnetic' layout={{ visibility: `${layerVisibility.magnetic ? "visible" : "none"}` }} source='magnetic' type='raster' beforeId='anchor-base-top' />
              </Source>

              {/* Everything above here is a base map. */}
              {/* anchor-base-top is declared with the source above. */}


              {/* DEA change & vegetation + interpretive terrain overlays (M4/M5).
                  Additive rasters on top of whatever base is showing, sharing
                  one opacity — the same pattern as the ASTER group below.
                  Every layer/style pair here was checked against the live
                  service; a dated product with no Time serves blank tiles, so
                  only ga_s2m_ard_3 follows the time bar and the rest are
                  composites or annual summaries. */}
              {DEA_OVERLAYS.map(o => {
                // Hidden overlays must not render even if a saved preset has
                // them switched on -- the sidebar no longer offers a way off.
                if (o.id === 'deaBarestEarth' && !SHOW_BAREST_EARTH) return null;
                if (!SHOW_VEG_WATER && VEG_WATER_DEA_IDS.includes(o.id)) return null;
                const tiles = buildDeaOverlayTiles(o, targetSentinelDate, deaOptions);
                if (!tiles) return null;
                return (
                  <Source
                    key={o.id}
                    id={o.id}
                    type='raster'
                    scheme={o.wms ? 'tms' : 'xyz'}
                    tileSize={256}
                    maxzoom={SENTINEL_MAX_ZOOM}
                    attribution={DEA_ATTRIBUTION}
                    tiles={tiles}
                  >
                    <Layer
                      id={o.id}
                      source={o.id}
                      type='raster'
                      beforeId='anchor-overlay-top'
                      {...(o.minzoom ? { minzoom: o.minzoom } : {})}
                      layout={{ visibility: deaVisibility[o.id] ? 'visible' : 'none' }}
                      // Each DEA layer has its own opacity (deaOptions.opacity, set
                      // under its sidebar row); the old shared deaOpacity is only
                      // the fallback for a layer never adjusted.
                      paint={{ 'raster-opacity': deaOptions?.opacity?.[o.id] ?? deaOpacity }}
                    />
                  </Source>
                );
              })}

              {/* Terrain (M5). Shaded relief from terrarium-encoded elevation
                  tiles, above the base rasters on purpose: it gives the geology
                  and magnetics mosaics real relief. The same source would drive
                  3D terrain (map.setTerrain) if that is ever wanted. */}
              {terrain.hillshade && (
                <Source
                  id='terrain-dem'
                  type='raster-dem'
                  tileSize={256}
                  encoding='terrarium'
                  maxzoom={TERRAIN_MAX_ZOOM}
                  attribution={TERRAIN_ATTRIBUTION}
                  tiles={TERRAIN_TILES}
                >
                  <Layer
                    id='terrain-hillshade'
                    source='terrain-dem'
                    type='hillshade'
                    beforeId='anchor-overlay-top'
                    layout={{ visibility: terrain.hillshade ? 'visible' : 'none' }}
                    paint={{ 'hillshade-exaggeration': 0.45, 'hillshade-shadow-color': '#2b2118' }}
                  />
                </Source>
              )}

              {/* ASTER mineral-map overlays — National ASTER Geoscience Maps
                  (CSIRO / Geoscience Australia) via NCI GSKY WMS. Interpretive
                  rasters blended on top of the base map with a shared opacity.
                  WMS 1.1.1 + srs/bbox EPSG:3857 to match our existing raster
                  pattern (avoids 1.3.0 lat,lon axis-order swap). */}
              {ASTER_LAYERS.map((a) => (
                <Source
                  key={a.id}
                  id={a.id}
                  scheme='tms'
                  tileSize={256}
                  type='raster'
                  attribution={'© CSIRO / Geoscience Australia — National ASTER Geoscience Maps'}
                  tiles={[`https://gsky.nci.org.au/ows/aster?service=WMS&request=GetMap&version=1.1.1&layers=${a.wms}&styles=&format=image/png&transparent=true&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}`]}
                >
                  <Layer
                    id={a.id}
                    source={a.id}
                    type='raster'
                    beforeId='anchor-overlay-top'
                    layout={{ visibility: asterVisibility[a.id] ? 'visible' : 'none' }}
                    paint={{ 'raster-opacity': asterOpacity }}
                  />
                </Source>
              ))}

              <AttributionControl compact={true} position='bottom-left' />

              {/* Optimized overlay components */}
              {/* Land Change sits last among the rasters, on purpose.
                  MapLibre draws in insertion order, so declaring it earlier let
                  any base map chosen afterwards -- the geological mosaics, the
                  topographic base, even the barest-earth backdrop -- paint
                  straight over the detected polygons. Anything that must stay
                  visible above the base has to be declared after all of them,
                  including the imagery the popup navigates to. */}
              {/* Land Change — polygons of detected vegetation loss, served from
                  our own PostGIS through GeoServer. WMS in EPSG:3857 keeps the
                  4326 axis-order question out of it entirely. */}
              {SHOW_LAND_CHANGE && landChange.visible && (
                <Source
                  id='land-change'
                  type='raster'
                  scheme='tms'
                  tileSize={256}
                  attribution={'Land change derived from ' + DEA_ATTRIBUTION}
                  tiles={buildLandChangeTiles()}
                >
                  <Layer
                    id='land-change'
                    source='land-change'
                    type='raster'
                    /* Its own anchor, above anchor-overlay-top: a DEA or ASTER
                       raster switched on later would otherwise insert directly
                       under anchor-overlay-top and bury the change polygons. */
                    beforeId='anchor-landchange-top'
                    layout={{ visibility: 'visible' }}
                    paint={{ 'raster-opacity': landChange.opacity ?? 0.85 }}
                  />
                </Source>
              )}

              {/* Georeferenced map plates (local only). Pinned by beforeId to
                  anchor-plates-top, so where this sits does not set its depth. */}
              <PlatesOverlay overlayVisibility={overlayVisibility} />
              {/* The plate list on the left: per-plate show/hide and opacity. */}
              <PlatesPanel overlayVisibility={overlayVisibility} />

              {/* Everything above here is a raster overlay; vectors follow. */}
              {/* anchor-overlay-top is declared with the source above. */}

              <TenementOverlayMapLibreOptimized
                tenementFilters={tenementFilters}
                overlayVisibility={overlayVisibility}
                selectedTenement={selectedTenement}
                geoJson={tenementGeoJson}
                setGeojson={setTenementGeojson} />

              <AssayOverlayMapLibreOptimized
                overlayVisibility={overlayVisibility}
                assayMarkerColourSettings={assayMarkerColourSettings}
                assayGeoJson={assayGeoJson}
                setAssayGeojson={setAssayGeojson}
                assayFilters={assayFilters} />

              <WaGeologyOverlay overlayVisibility={overlayVisibility} />

              <MinedexOverlayMapLibre
                overlayVisibility={overlayVisibility}
                minedexFilters={minedexFilters} />

              {/* Admin only, and it checks that itself — see useIsAdmin. Renders
                  nothing at all for everyone else, including the fetch. */}
              <OcrGoldOverlay overlayVisibility={overlayVisibility} />
              <AsxAlluvialOverlay overlayVisibility={overlayVisibility} />
              <ProspectOverlay overlayVisibility={overlayVisibility} prospectFilters={prospectFilters} />

              {/* The 10 km block grid a report is commissioned against.
                  Computed from the viewport, not fetched. */}
              {/* The 10 km report-block grid's sidebar checkbox was removed, so it is
                  forced off here - a preset saved with it on would otherwise draw
                  a grid nothing on screen can switch off. */}
              <BlockGridOverlay overlayVisibility={{ ...overlayVisibility, blocks: false }} />

              <DrillHoleOverlayMapLibreOptimized
                drillHoleMarkerColourSettings={drillHoleMarkerColourSettings}
                overlayVisibility={overlayVisibility}
                drillHoleGeoJson={drillHoleGeoJson}
                setDrillHoleGeoJson={setDrillHoleGeoJson}
                drillHoleFilters={drillHolefilters} />

              <GraticlesOverlayLibre
                graticlesGeoJSON={graticleGeoJson}
                overlayVisibility={overlayVisibility}
                selectedGraticleStack={selectedGraticleStack} />

              <Section19OverlayLibre
                overlayVisibility={overlayVisibility} />

              <AmalgamationsPendingOverlay
                overlayVisibility={overlayVisibility}
                selectedAmalgamation={selectedAmalgamation}
                geoJson={amalgamationsPendingGeoJson}
                setGeoJson={setAmalgamationsPendingGeoJson}
                // Drawn only where the (filtered, searched) tenement layer has
                // the tenement; its details feed the popup.
                tenementGeoJson={tenementGeoJson} />

              {/* Popups */}
              {showTenementPopup ? tenementPopup : <></>}
              {showGeologyPopup ? geologyPopup : <></>}
              {showAssayPopup ? assayPopup : <></>}
              {showMinedexPopup ? minedexPopup : <></>}
              {showGraticlePopup ? graticlePopup : <></>}
              {showSection19Popup ? section19Popup : <></>}
              {showDrawPointPopup ? drawPointPopup : <></>}
              {showDrillHolePopup ? drillHolePopup : <></>}

              {/* Search over the datasets as well as place names - see
                  search/searchSources.js. Telemetry stays hit-counts only; the
                  query string is a place the user is interested in and is not
                  sent anywhere. */}
              <div className="geocoding" style={{ top: 16, left: 16, position: 'absolute' }}>
                <MapSearch
                  apiKey={API_KEY}
                  onPick={handleSearchPick}
                  onArrive={handleSearchArrive}
                />
              </div>

              {/* Floating Controls */}
              <FloatingMenu onOpenAccountSettings={onOpenAccountSettings} />
              {!is40ESideBarShown && (
                <SidebarToggle
                  isSideBarShown={isSideBarShown}
                  onToggle={onToggleSidebar}
                />
              )}
            </Map>

            {/* Imagery age — one honest answer per base layer (M3) */}
            {SHOW_IMAGERY_AGE_CHIP && (mapZoom === null || mapZoom >= SENTINEL_MIN_ZOOM) && !mobileFiltersOpen && (
              <ImageryAgeChip
                latestClear={latestClear}
                alreadyShowing={!!layerVisibility.sentinel2Dated}
                onShowSentinel={showSentinelLayer}
                // Clear the time bar when it is up: the bar's top edge sits
                // 134px above the viewport bottom, so 118 overlapped it by 15px.
                bottomOffset={sentinelEnabled ? 146 : 44}
              />
            )}

            {/* Legend for the derived land-change polygons, in the map where it
                can be read against them. */}
            {SHOW_LAND_CHANGE && landChange.visible && !mobileFiltersOpen && (
              <LandChangeLegend bottomOffset={SHOW_SENTINEL_TIME_BAR && sentinelEnabled ? 118 : 44} />
            )}

            {/* Cursor MGA readout - same right inset as the legend */}
            {!mobileFiltersOpen && (
              <CursorPosition mga={mousePoint} latLng={mouseLngLat} bottomOffset={10} />
            )}

            {/* Time bar — only for the dated layer, and only where it applies.
                TEMPORARILY HIDDEN: flip SHOW_SENTINEL_TIME_BAR back to true to
                restore it. Nothing else about the dated Sentinel-2 layer is
                disabled — picking a date still works, the picker is just not
                on screen. See web-app/docs/TEMPORARILY-DISABLED.md. */}
            {SHOW_SENTINEL_TIME_BAR && sentinelEnabled && !mobileFiltersOpen && (mapZoom === null || mapZoom >= SENTINEL_MIN_ZOOM) && (
              <SentinelTimeBar
                dates={sentinelDates}
                loading={sentinelLoading}
                selectedDate={targetSentinelDate}
                onSelectDate={handleSelectImageryDate}
                styleId={imageryPrefs.styleId}
                onStyleChange={styleId => updateImageryPrefs({ styleId })}
                sensor={imagerySensor}
                onSensorChange={id =>
                  updateImageryPrefs({
                    sensor: id,
                    styleId: styleForSensor(id, imageryPrefs.styleId),
                    // The remembered date rarely exists on the other sensor;
                    // the resolver snaps to the nearest and says so.
                  })
                }
                maxCloud={imageryPrefs.maxCloud}
                onMaxCloudChange={maxCloud => updateImageryPrefs({ maxCloud })}
                rangeId={imageryPrefs.rangeId}
                onRangeChange={rangeId => updateImageryPrefs({ rangeId })}
                snapNote={imagerySnapNote}
                playing={imageryPlaying}
                onPlayingChange={setImageryPlaying}
              />
            )}

            {/* Mobile Layers Toggle - positioned in top-right of map area */}
            <MobileSidebarToggle
              isOpen={mobileFiltersOpen}
              onToggle={onMobileFiltersToggle}
            />
          </div>

          <div className={isSideBarShown ? '' : ' active do-not-render-this'} id="sidebar">
            {!mobileFiltersOpen && <SideBar
              setTenementFilters={setTenementFilters}
              tenementFilters={tenementFilters}
              layerVisibility={layerVisibility}
              setLayerVisibility={setLayerVisibility}
              asterVisibility={asterVisibility}
              setAsterVisibility={setAsterVisibility}
              asterOpacity={asterOpacity}
              setAsterOpacity={setAsterOpacity}
              deaVisibility={deaVisibility}
              setDeaVisibility={setDeaVisibility}
              deaOpacity={deaOpacity}
              setDeaOpacity={setDeaOpacity}
              deaOptions={deaOptions}
              setDeaOptions={setDeaOptions}
              landChange={landChange}
              setLandChange={setLandChange}
              terrain={terrain}
              setTerrain={setTerrain}
              overlayVisibility={overlayVisibility}
              setOverlayVisisbility={setOverlayVisisbility}
              setTenementPopup={setTenementPopup}
              setShowTenementPopup={setShowTenementPopup}
              assayMarkerColourSettings={assayMarkerColourSettings}
              setAssayMarkerColourSettings={setAssayMarkerColourSettings}
              assayFilters={assayFilters}
              setAssayFilters={setAssayFilters}
              assayGeoJson={assayGeoJson}
              setAssayGeoJson={setAssayGeojson}
              minedexFilters={minedexFilters}
              setMinedexFilters={setMinedexFilters}
              drillHoleFilters={drillHolefilters}
              setDrillHoleFilters={setDrillHoleFilters}
              drillHoleColourSettings={drillHoleMarkerColourSettings}
              setDrilHoleColourSettings={setDrillHoleMarkerColourSettings}
              drillHoleGeoJson={drillHoleGeoJson}
              prospectFilters={prospectFilters}
              setProspectFilters={setProspectFilters}
              trigger40EWizard={trigger40EWizard}
              drawCtrl={drawCtrl}
                waypointRevision={waypointRevision}
              presetsControl={
                <PresetsControl
                  presets={presets}
                  activePresetId={activePresetId}
                  setActivePresetId={setActivePresetId}
                  isLoading={isPresetsLoading}
                  userLoggedIn={presetsUserLoggedIn}
                  canSaveMore={canSaveMore}
                  userPresetCount={userPresetCount}
                  maxUserPresets={maxUserPresets}
                  buildCurrentPayload={buildCurrentPayload}
                  savePresetAsNew={savePresetAsNew}
                  updatePreset={updatePreset}
                  renamePreset={renamePreset}
                  deletePreset={deletePreset}
                  onError={onPresetError}
                />
              }
            />}
          </div>

          <div className={is40ESideBarShown ? 'tab-content' : 'tab-content active do-not-render-this'} id="sidebar40e">
            <SideBar40E
              setIs40ESideBarShown={setIs40ESideBarShown}
              is40ESideBarShown={is40ESideBarShown}
              tenement40E={tenement40E}
              setOverlayVisisbility={setOverlayVisisbility}
              overlayVisibility={overlayVisibility}
              selectedGraticleStack={selectedGraticleStack}
              setSelectedGraticuleStack={setSelectedGraticuleStack}
              setAlerts={setAlerts}
              setSideBarShown={setSideBarShown}
            />
          </div>

        <SampleReporter reportedAssay={reportedAssay} show={reportAssayModalShow} onHide={() => setReportAssayModalShow(false)} />
        <DHSampleReporter reportedAssay={reportedDh} show={reportDhModalShow} onHide={() => setReportDhModalShow(false)} />
        
        {/* 40E Confirmation Modal */}
        <Modal show={show40EConfirmModal} onHide={cancel40EWizard} centered>
          <Modal.Header closeButton>
            <Modal.Title>Create Section 40E Permit</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to create a Section 40E permit for tenement <strong>{pendingTenement40E?.properties?.fmt_tenid}</strong>?</p>
            <p>This will open the permit wizard and enable graticule selection mode.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cancel40EWizard}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirm40EWizard}>
              Create Permit
            </Button>
          </Modal.Footer>
        </Modal>
      </MapProvider>
    </>
  );
}